import React, { useEffect, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TrashIcon } from '@pkg/assets/icons/trash.svg';
import { Sheet } from '@pkg/components';
import { Badge } from '@pkg/components/Sections/Profile/Badge';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useAppSelector } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { type SearchParamsItemAction } from '@app/client/resources/yat-pages/page-items';

import {
    BaseActionsWrapper,
    ConfirmationWrapper,
    CopyWrapper,
    RemoveActionWrapper,
    StyledAddButton,
    StyledButton,
    ToastActionsWrapper,
} from './styles';

export interface AddAndPreviewProps {
    handleSelectedItemsAction: (action: SearchParamsItemAction) => void;
    handleToggleSelectAllItems?: () => void;
    selectedItems: string[];
    selectedCount: number;
    selectedAllItems?: boolean;
}

export function AddAndPreview({
    handleSelectedItemsAction,
    handleToggleSelectAllItems,
    selectedItems,
    selectedCount,
    selectedAllItems,
}: AddAndPreviewProps) {
    const { tablet } = useMediaQuery();
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [isLoadingBatchQuery, setIsLoadingBatchQuery] = useState(false);
    const { t: translate } = useTranslation(['yat_nft_manager']);

    const liquidIDSelectedItem = useAppSelector((state) => state.yatPage.liquidIDSelectedItem);
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);
    const selectInPageItems = useAppSelector((state) => state.nftManager.selectInPageItems);

    useEffect(() => {
        setConfirmDelete(false);
    }, [selectedItems]);

    const toggleConfirmation = () => setConfirmDelete(!confirmDelete);

    const handleAddItems = () => {
        handleSelectedItemsAction('Add');
        if (selectedAllItems) {
            setIsLoadingBatchQuery(true);
        }
    };

    const handleRemoveItems = () => {
        handleSelectedItemsAction('Remove');
        if (selectedAllItems) {
            setIsLoadingBatchQuery(true);
        }
    };

    const buttonActionCopy = selectInPageItems
        ? translate('yat_nft_manager_remove_nft')
        : translate('yat_nft_manager_add_and_preview');

    const removeConfirmationMarkup = (
        <ConfirmationWrapper
            key="confirmation"
            initial={{ y: '200%' }}
            animate={{ y: 0 }}
            exit={{ y: '200%' }}
            transition={{ duration: 0.3 }}
        >
            <CopyWrapper>{translate('yat_nft_manager_confirmation')}</CopyWrapper>
            <div>
                <Button
                    size="small"
                    variant="solid"
                    onClick={handleRemoveItems}
                    color="warning"
                    loading={isLoadingBatchQuery}
                >
                    {translate('yat_nft_manager_remove')}
                </Button>
                <Button size="small" variant="solid" color="clear" onClick={toggleConfirmation}>
                    {translate('yat_nft_manager_cancel')}
                </Button>
            </div>
        </ConfirmationWrapper>
    );

    const desktopActionsMarkup = (
        <ToastActionsWrapper
            key="actions"
            initial={{ y: '200%' }}
            animate={{ y: 0 }}
            exit={{ y: '200%' }}
            transition={{ ease: 'anticipate' }}
        >
            <Sheet.ActionToast.Selected
                selectedAllItems={selectedAllItems}
                handleToggleSelectAllItems={handleToggleSelectAllItems}
                count={selectedCount}
            />

            <BaseActionsWrapper>
                <StyledAddButton
                    size="small"
                    color={selectInPageItems ? 'warning' : 'white'}
                    variant="solid"
                    onClick={selectInPageItems ? toggleConfirmation : handleAddItems}
                    loading={isLoadingBatchQuery}
                >
                    {buttonActionCopy}
                    {selectedCount > 1 ? 's' : ''}
                </StyledAddButton>

                <AnimatePresence>
                    {selectedAllItems ? (
                        <RemoveActionWrapper initial={{ width: 0 }} animate={{ width: 40 }} exit={{ width: 0 }}>
                            <TrashIcon onClick={toggleConfirmation} />
                        </RemoveActionWrapper>
                    ) : null}
                </AnimatePresence>
            </BaseActionsWrapper>
        </ToastActionsWrapper>
    );

    const desktopToastContent = confirmDelete ? removeConfirmationMarkup : desktopActionsMarkup;

    const itemActionToast =
        !editingLiquidID && selectedCount ? (
            <Sheet.ActionToast
                wide={selectedAllItems || (selectInPageItems && confirmDelete)}
                key="NftManagerPanel-ActionToast-Item"
            >
                {tablet ? (
                    <AnimatePresence mode="wait">{desktopToastContent}</AnimatePresence>
                ) : (
                    <Button
                        color={selectInPageItems ? 'error' : 'primary'}
                        onClick={selectInPageItems ? handleRemoveItems : handleAddItems}
                        endIcon={<Badge variant="round">{selectedCount}</Badge>}
                    >
                        {buttonActionCopy}
                        {selectedCount > 1 ? 's' : ''}
                    </Button>
                )}
            </Sheet.ActionToast>
        ) : null;

    const liquidIdActionToast =
        liquidIDSelectedItem && editingLiquidID && !tablet ? (
            <Sheet.ActionToast key="NftManagerPanel-ActionToast-LiquidId">
                <StyledButton onClick={handleAddItems}>
                    {translate('yat_nft_manager_micro_menu_add_liquid_id')}
                </StyledButton>
            </Sheet.ActionToast>
        ) : null;

    return (
        <AnimatePresence>
            {itemActionToast}
            {liquidIdActionToast}
        </AnimatePresence>
    );
}
